import { Breadcrumb, Button, Col, Flex, Form, Input, Row, Select, Space, Card } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { MainLayout } from "components/layouts";
import { useEffect, useRef } from "react";
import styles from "../styles/SettingEmailTemplateForm.module.css";
import { useSelector } from "react-redux";
import RichTextEditor from "./RichTextEditor";
import { useTranslation } from "react-i18next";

const LOGO_CODE = "company_logo";

// mock ข้อมูลตัวอย่างสำหรับ preview เท่านั้น ไม่ต้อง i18n
const SAMPLE_DATA = {
    employee_name: "สมชาย ใจดี",
    employee_no: "A0012",
    position: "เจ้าหน้าที่ฝ่ายบุคคล",
    department: "ฝ่ายทรัพยากรบุคคล",
    manager_name: "สมหญิง มั่นคง",
    probation_end_date: "30 ก.ย. 2569",
    date_of_birth: "15 ม.ค. 2538",
    employment_date: "1 มี.ค. 2567",
    company_name: "บริษัท Payroll จำกัด",
};
const EMPTY = {};
const SettingEmailTemplateForm = ({
    isEdit = false,
    initialValues = EMPTY,
    loading = false,
    onSubmit = () => { },
    onCancel = () => { },
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const editorRef = useRef(null);
    const subject = Form.useWatch("subject", form);
    const message = Form.useWatch("message", form);
    const currentCompanyProfile = useSelector((state) => state.company.currentCompanyProfile) || {};
    const logoSrc = currentCompanyProfile.companyLogo
        ? "data:image/png;base64, " + currentCompanyProfile.companyLogo
        : "";

    const VARIABLES = [
        { code: "employee_name", label: t("setting_email_template_list.form.variable_employee_name") },
        { code: "employee_no", label: t("setting_email_template_list.form.variable_employee_no") },
        { code: "position", label: t("setting_email_template_list.form.variable_position") },
        { code: "department", label: t("setting_email_template_list.form.variable_department") },
        { code: "manager_name", label: t("setting_email_template_list.form.variable_manager_name") },
        { code: "probation_end_date", label: t("setting_email_template_list.form.variable_probation_end_date") },
        { code: "date_of_birth", label: t("setting_email_template_list.form.variable_date_of_birth") },
        { code: "employment_date", label: t("setting_email_template_list.form.variable_employment_date") },
        { code: "company_name", label: t("setting_email_template_list.form.variable_company_name") },
        { code: LOGO_CODE, label: t("setting_email_template_list.form.variable_company_logo") },
    ];

    function renderPreviewText(text) {
        return VARIABLES.filter((v) => v.code !== LOGO_CODE).reduce(
            (acc, v) => acc.replaceAll(`{{${v.code}}}`, SAMPLE_DATA[v.code] ?? ""),
            text ?? ""
        );
    }
    function renderPreviewHtml(html) {
        return VARIABLES.filter((v) => v.code !== LOGO_CODE).reduce(
            (acc, v) => acc.replaceAll(`{{${v.code}}}`, SAMPLE_DATA[v.code] ?? ""),
            html ?? ""
        );
    }
    function renderPreviewBody(html, logoImgSrc = "") {
        const withVars = renderPreviewHtml(html);
        const imgTag = logoImgSrc
            ? `<img src="${logoImgSrc}" alt="logo" style="width:48px;height:48px;object-fit:contain;border-radius:4px;vertical-align:middle;margin:0 4px;" />`
            : `<span style="display:inline-block;width:48px;height:48px;background:#f0f0f0;border:1px dashed #ccc;border-radius:4px;vertical-align:middle;margin:0 4px;"></span>`;
        return withVars.replaceAll(`{{${LOGO_CODE}}}`, imgTag);
    }

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [initialValues, form]);

    const insertVariable = (code) => {
        editorRef.current?.insertVariable(code);
    };

    return (
        <MainLayout>
            <Space direction="vertical" size={24} style={{ width: "100%" }}>
                <Flex vertical>
                    <Breadcrumb
                        items={[
                            { title: t("home.header"), href: "/" },
                            { title: t("masterdata.header"), href: "" },
                            { title: t("setting_email_template_list.header"), href: "/masterdata/setting_email_template" },
                            { title: isEdit ? t("setting_email_template_list.form.breadcrumb_edit") : t("setting_email_template_list.form.breadcrumb_create") },
                        ]}
                    />
                    <h2>{isEdit ? t("setting_email_template_list.form.title_edit") : t("setting_email_template_list.form.title_create")}</h2>
                    <span style={{ color: "#667085" }}>{t("setting_email_template_list.form.subtitle")}</span>
                </Flex>

                <Row gutter={24}>
                    <Col span={14}>
                        <Card className={styles.formCard}>
                            <Form form={form} layout="vertical" onFinish={onSubmit}>
                                <Row gutter={16}>
                                    <Col span={16}>
                                        <Form.Item
                                            name="emailTemplateCode"
                                            label={t("setting_email_template_list.label.email_event")}
                                            rules={[{ required: false, message: t("setting_email_template_list.form.event_required") }]}
                                            extra={t("setting_email_template_list.form.event_extra")}
                                        >
                                            <Input size="large" disabled={isEdit} placeholder={t("setting_email_template_list.form.event_placeholder")} />
                                        </Form.Item>
                                    </Col>
                                    <Col span={8}>
                                        <Form.Item
                                            name="isHr"
                                            label={t("setting_email_template_list.label.recipient")}
                                            rules={[{ required: true, message: t("setting_email_template_list.form.recipient_required") }]}
                                            extra={isEdit ? t("setting_email_template_list.form.recipient_locked_extra") : t("setting_email_template_list.form.recipient_extra")}
                                        >
                                            <Select
                                                size="large"
                                                disabled={isEdit}
                                                placeholder={t("setting_email_template_list.form.recipient_placeholder")}
                                                options={[
                                                    { label: t("setting_email_template_list.form.recipient_hr"), value: true },
                                                    { label: t("setting_email_template_list.form.recipient_employee"), value: false },
                                                ]}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item
                                    name="templateName"
                                    label={t("setting_email_template_list.label.template_name")}
                                    rules={[{ required: true, message: t("setting_email_template_list.form.template_name_required") }]}
                                    extra={t("setting_email_template_list.form.template_name_extra")}
                                >
                                    <Input size="large" placeholder={t("setting_email_template_list.form.template_name_placeholder")} />
                                </Form.Item>
                                <Form.Item
                                    name="subject"
                                    label={t("setting_email_template_list.label.subject")}
                                    rules={[{ required: true, message: t("setting_email_template_list.form.subject_required") }]}
                                >
                                    <Input size="large" placeholder={t("setting_email_template_list.form.subject_placeholder")} />
                                </Form.Item>

                                <Form.Item
                                    name="message"
                                    label={t("setting_email_template_list.form.message_label")}
                                    rules={[{ required: true, message: t("setting_email_template_list.form.message_required") }]}
                                >
                                    <RichTextEditor ref={editorRef} />
                                </Form.Item>

                                <div style={{ marginBottom: 24 }}>
                                    <div style={{ marginBottom: 8, color: "#667085" }}>
                                        {t("setting_email_template_list.form.variables_hint")}
                                    </div>
                                    <Space wrap>
                                        {VARIABLES.map((v) => (
                                            <Button key={v.code} size="small" onClick={() => insertVariable(v.code)}>
                                                {v.code === LOGO_CODE ? `🖼 {{${v.code}}}` : `{{${v.code}}}`}
                                            </Button>
                                        ))}
                                    </Space>
                                </div>

                                <Space>
                                    <Button onClick={onCancel} style={{ height: 40, borderRadius: 8 }}>
                                        {t("action.cancel")}
                                    </Button>
                                    <Button type="primary" htmlType="submit" loading={loading} style={{ height: 40, borderRadius: 8 }}>
                                        {t("action.save")}
                                    </Button>
                                </Space>
                            </Form>
                        </Card>
                    </Col>

                    <Col span={10}>
                        <Card
                            className={styles.previewCard}
                            title={t("setting_email_template_list.form.preview_title")}
                        // extra={<Button size="small" icon={<EyeOutlined />}>{t("setting_email_template_list.form.preview_change_sample")}</Button>}
                        >
                            <div style={{ color: "#667085", fontSize: 13, marginBottom: 16 }}>
                                {t("setting_email_template_list.form.preview_hint")}
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <div><b>{t("setting_email_template_list.form.preview_from")}</b> hr@yourcompany.com</div>
                                <div><b>{t("setting_email_template_list.form.preview_to")}</b> somchai.j@yourcompany.com</div>
                                <div><b>{t("setting_email_template_list.form.preview_subject")}</b> {renderPreviewText(subject)}</div>
                            </div>
                            <Card className={styles.previewBody}>
                                <div dangerouslySetInnerHTML={{ __html: renderPreviewBody(message, logoSrc) }} />
                            </Card>
                        </Card>
                    </Col>
                </Row>
            </Space>
        </MainLayout>
    );
};

export default SettingEmailTemplateForm;